import * as React from "react";
import { cn } from "@/lib/utils";

// v2 Card — 12px radius, theme-aware elevation, border from --border.
// Children: CardHeader, CardTitle, CardBody, CardFooter keep the same prop
// API as v1. `interactive` opts a card into the shared hover treatment
// (slight lift + raised shadow, 150ms) so every clickable card on the
// platform answers the cursor the same way.

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
}

export const Card: React.FC<CardProps> = ({ className, interactive, ...props }) => (
  <div
    className={cn(
      "rounded-lg border border-border bg-surface shadow-card",
      interactive &&
        "transition-[transform,box-shadow,border-color] duration-150 ease-out hover:-translate-y-px hover:border-border-2 hover:shadow-raised",
      className,
    )}
    {...props}
  />
);

export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "px-5 pt-4 pb-3 flex items-baseline justify-between gap-3",
      className,
    )}
    {...props}
  />
);

export const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({
  className,
  ...props
}) => (
  <h3
    className={cn("font-display text-lg font-semibold text-text leading-snug", className)}
    {...props}
  />
);

export const CardBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn("px-5 py-4", className)} {...props} />;

export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "px-5 py-3 border-t border-border flex items-center gap-2",
      className,
    )}
    {...props}
  />
);
